import { MessageSquareText } from "lucide-react";
import { trackEvent } from "@/lib/analytics-events";
import { getAttribution, resolveRefCode } from "@/lib/attribution";
import { campaignPage, withRef } from "@/lib/campaign-pages";
import { useRouterState } from "@tanstack/react-router";
import { beacon, getDevice, getSessionId } from "@/lib/lead-tracking";
import { smsHref } from "@/components/TextUs";
import { WhatsAppIcon } from "@/components/ContactButtons";
import { whatsappHref } from "@/lib/contact";

export const CAMPAIGN_CTA_LABEL = "Text Maria · Check My Date";

function baseLead(refCode?: string) {
  const a = getAttribution();
  return {
    session_id: getSessionId(),
    utm_source: a.utm_source, utm_medium: a.utm_medium, utm_campaign: a.utm_campaign, utm_content: a.utm_content,
    ref_code: resolveRefCode(refCode),
    gclid: a.gclid, landing_page: a.landing_page, referrer: a.referrer, first_touch_at: a.first_touch_at,
    page_path: window.location.pathname, device: getDevice(),
  };
}

export function campaignSmsBody(refCode?: string) {
  return withRef("Hi Maria! I'd like to check my date.\nDate: __\nGroup size: __\nPickup area: __\nOccasion: __", resolveRefCode(refCode));
}

/**
 * Campaign CTA (tagged visits). No form: phones open a prefilled text, and WhatsApp works on every device.
 * Each tap logs a Lead Intent (never a conversion) and fires text_us_click / whatsapp_click.
 */
export default function CampaignCta({ refCode, className = "" }: { refCode?: string; className?: string }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const page = campaignPage(path);
  const label = page?.label ?? CAMPAIGN_CTA_LABEL;
  const fallbackBody = withRef("Hi Maria! I'd like to check my date.", refCode ?? page?.ref);

  const onText = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const ref_code = resolveRefCode(refCode);
    e.currentTarget.href = smsHref(campaignSmsBody(refCode));
    beacon("/api/public/lead", { kind: "intent", ...baseLead(refCode) });
    trackEvent("text_us_click", { link_location: "hero", ref_code });
  };
  const onWhatsApp = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.href = whatsappHref(campaignSmsBody(refCode));
    beacon("/api/public/lead", { kind: "intent", ...baseLead(refCode) });
  };

  return (
    <>
      <a href={smsHref(fallbackBody)} onClick={onText} data-track-manual
        className={`sms-touch-only btn-primary-hero rounded-sm text-center inline-flex items-center justify-center gap-2 ${className}`}>
        <MessageSquareText className="w-4 h-4" /> {label}
      </a>
      <a href={whatsappHref(fallbackBody)} onClick={onWhatsApp} target="_blank" rel="noopener noreferrer"
        className={`btn-outline-hero rounded-sm text-center inline-flex items-center justify-center gap-2 ${className}`}>
        <WhatsAppIcon /> WhatsApp Maria
      </a>
    </>
  );
}
